/**
 * @file いま出しているページに関係する AI 出力を、操作ごとに数える。
 *
 * @what: 家族の AI 出力を引き、ページ・メモ・問のどれかに当たるものだけを残して
 *   「AI 解説」「AI 採点」ごとの件数を返す。0件の操作は返さない。
 * @why: 件数はヘッダーのボタンにも、狭い画面の⋯のメニューにも必要である。数える所を1つにして、
 *   出し方だけを置き場所ごとに変える。
 * @failure: 取得に失敗したら空にする (リーダーの邪魔をしない。詳細は console)。
 */

import { useEffect, useMemo, useState } from "react";
import {
  ASK_PRESET_LABELS,
  askRecordScope,
  fetchAskRecords,
  type StudyAskRecordSummary,
} from "../api/askRecords";

/** ボタンに出す操作の並び (押すボタンと同じ言葉、同じ順)。 */
const OPS: readonly { preset: string; label: string }[] = [
  { preset: "ai_explain", label: ASK_PRESET_LABELS.ai_explain! },
  { preset: "ai_grade", label: ASK_PRESET_LABELS.ai_grade! },
];

export interface PageAskCount {
  preset: string;
  label: string;
  count: number;
}

export function usePageAskCounts({
  familyId,
  personId,
  sourceId,
  page,
  noteIds = [],
  problemIds = [],
  reloadKey = 0,
}: {
  familyId: string;
  personId: string;
  sourceId: string;
  /** 表示中のページ (リーダーの連番)。 */
  page: number;
  noteIds?: readonly string[];
  problemIds?: readonly string[];
  /** 数え直す合図である (質問欄から聞いた直後に増やす)。 */
  reloadKey?: number;
}): readonly PageAskCount[] {
  const [records, setRecords] = useState<StudyAskRecordSummary[] | null>(null);

  useEffect(() => {
    // まなぶ人が決まる前は引かない。※なぜなら決まったあとにもう一度引くことになり、
    //   誰のものでもない1回ぶんが無駄になるためである。
    if (personId === "") {
      setRecords(null);
      return;
    }
    const controller = new AbortController();
    fetchAskRecords(familyId, personId, controller.signal)
      .then((rs) => {
        if (!controller.signal.aborted) setRecords(rs);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to count ask records for the page", err);
        setRecords(null);
      });
    return () => controller.abort();
  }, [familyId, personId, reloadKey]);

  return useMemo(() => {
    if (records === null) return [];
    const here = records.filter(
      (r) => askRecordScope(r.targets, sourceId, page, noteIds, problemIds) !== null,
    );
    return OPS.map((op) => ({
      ...op,
      count: here.filter((r) => r.first_preset === op.preset).length,
    })).filter((op) => op.count > 0);
  }, [records, sourceId, page, noteIds, problemIds]);
}
